import { useMemo, useState, type ReactNode } from "react";
import { Check, Download, FileText, GitBranch, MessageSquareText, Target, Users } from "lucide-react";
import { useStore } from "../lib/store";
import {
  ALL_EXPORT_GROUPS,
  EXPORT_GROUPS,
  exportTable,
  rowsToCsv,
  type ExportGroupId,
  type LeadRow,
} from "../features/leads/list";
import { Dialog } from "../ui/Dialog";
import { Button } from "../ui/Button";
import { Field } from "../ui/Field";
import { Select } from "../ui/Select";
import "./export.css";

/**
 * Exportar leads ou clientes, no formato do protótipo: à esquerda o funil e os
 * grupos de dados; à direita quantos cadastros entram e uma amostra do arquivo.
 * A amostra sai da mesma tabela que o CSV (`exportTable`), então o que se
 * confere antes de baixar é o que o arquivo contém.
 */

const GROUP_ICON: Record<ExportGroupId, ReactNode> = {
  identity: <FileText aria-hidden="true" />,
  company: <GitBranch aria-hidden="true" />,
  contact: <MessageSquareText aria-hidden="true" />,
  commercial: <Target aria-hidden="true" />,
  people: <Users aria-hidden="true" />,
};

/** Na prévia resumida, as três colunas que mais identificam o cadastro. */
const PREFERRED = ["Nome", "Empresa", "E-mail", "Valor do cadastro (R$)"];
const SAMPLE = 5;

const count = new Intl.NumberFormat("pt-BR");

export function ExportDialog({
  open,
  onClose,
  rows,
  noun,
  fileName,
}: {
  open: boolean;
  onClose: () => void;
  /** A seleção inteira da lista, com os filtros aplicados — todas as páginas. */
  rows: readonly LeadRow[];
  /** "leads" ou "clientes". */
  noun: "leads" | "clientes";
  fileName: string;
}) {
  const store = useStore();
  const [funnel, setFunnel] = useState<string>("all");
  const [groups, setGroups] = useState<ExportGroupId[]>([...ALL_EXPORT_GROUPS]);
  const [allColumns, setAllColumns] = useState(false);

  const people = useMemo(
    () => ({ contacts: store.contacts ?? [], partyChannels: store.partyChannels ?? [] }),
    [store.contacts, store.partyChannels],
  );
  const funnels = store.funnels.filter((item) => !item.archived);
  const selected = useMemo(
    () => (funnel === "all" ? rows : rows.filter((row) => row.funnelId === funnel)),
    [rows, funnel],
  );
  const table = useMemo(
    () => exportTable(selected.slice(0, SAMPLE), groups, people),
    [selected, groups, people],
  );
  const headers = useMemo(() => table[0] ?? [], [table]);
  const columns = headers.length;
  const visible = useMemo(() => {
    const indices = headers.map((_, index) => index);
    if (allColumns) return indices;
    const preferred = PREFERRED.map((name) => headers.indexOf(name)).filter((index) => index >= 0);
    return [...new Set([...preferred, ...indices])].slice(0, 3).sort((a, b) => a - b);
  }, [headers, allColumns]);

  const toggle = (id: ExportGroupId) =>
    setGroups((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : ALL_EXPORT_GROUPS.filter((item) => item === id || current.includes(item)),
    );

  const download = () => {
    if (!groups.length || !selected.length || typeof URL.createObjectURL !== "function") return;
    const csv = rowsToCsv(selected, groups, people);
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    // Endereço temporário revogado após o uso (AGENTS §26.3).
    URL.revokeObjectURL(url);
    onClose();
  };

  const selectedLabel = noun === "clientes" ? "Clientes selecionados" : "Leads selecionados";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width="wide"
      className="cx-transfer"
      icon={<Download size={21} />}
      title={`Exportar ${noun}`}
      description="Escolha os campos e confira os cadastros antes de baixar."
      footer={
        <>
          <span className="cx-transfer-footnote" aria-live="polite">
            {count.format(selected.length)} {noun} · {columns} colunas
          </span>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={download} disabled={!groups.length || !selected.length}>
            Baixar CSV
          </Button>
        </>
      }
    >
      <div className="cx-transfer-layout">
        <section className="cx-transfer-config" aria-label="Configurar exportação">
          <Field label="Filtrar por funil">
            {({ id }) => (
              <Select
                id={id}
                label="Filtrar por funil"
                size="field"
                value={funnel}
                onChange={setFunnel}
                options={[
                  { value: "all", label: "Todos os funis" },
                  ...funnels.map((item) => ({ value: item.id, label: item.name })),
                ]}
              />
            )}
          </Field>
          <fieldset>
            <legend>Dados do arquivo</legend>
            {EXPORT_GROUPS.map((group) => {
              const on = groups.includes(group.id);
              return (
                <button
                  key={group.id}
                  type="button"
                  className="cx-transfer-choice"
                  aria-pressed={on}
                  onClick={() => toggle(group.id)}
                >
                  {GROUP_ICON[group.id]}
                  <span>
                    <strong>{group.label}</strong>
                    <small>{group.detail}</small>
                  </span>
                  <span className="cx-transfer-check" aria-hidden="true">
                    <Check />
                  </span>
                </button>
              );
            })}
          </fieldset>
          <p className="cx-transfer-note">
            Os filtros da lista são mantidos. A exportação inclui todas as páginas da seleção.
          </p>
        </section>

        <section className="cx-transfer-preview" aria-label="Prévia da exportação">
          <div className="cx-transfer-summary">
            <div>
              <span>{selectedLabel}</span>
              <b>{count.format(selected.length)}</b>
            </div>
            <Download size={29} aria-hidden="true" />
          </div>
          <div className="cx-transfer-preview-head">
            <h3>Prévia do arquivo</h3>
            <span className="cx-transfer-badge">CSV</span>
          </div>

          {!groups.length ? (
            <div className="cx-transfer-empty">
              <FileText size={24} aria-hidden="true" />
              <strong>Escolha os dados</strong>
              <p>Selecione pelo menos um grupo para montar o arquivo.</p>
            </div>
          ) : !selected.length ? (
            <div className="cx-transfer-empty">
              <FileText size={24} aria-hidden="true" />
              <strong>Nenhum cadastro nesta seleção</strong>
              <p>Ajuste o funil ou os filtros da lista para encontrar cadastros.</p>
            </div>
          ) : (
            <>
              <div
                className="cx-transfer-table-wrap"
                tabIndex={0}
                aria-label={`Amostra das colunas do CSV${allColumns ? " · role horizontalmente para ver todas" : ""}`}
              >
                <table className={allColumns ? "cx-transfer-table is-full" : "cx-transfer-table"}>
                  <thead>
                    <tr>
                      {visible.map((index) => (
                        <th key={index} scope="col">
                          {headers[index]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {table.slice(1).map((line, row) => (
                      <tr key={row}>
                        {visible.map((index) => (
                          <td key={index}>{line[index] || "—"}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="cx-transfer-preview-meta">
                <span>
                  {Math.min(SAMPLE, selected.length)} de {count.format(selected.length)} cadastros · {visible.length}{" "}
                  de {columns} colunas
                </span>
                {columns > 3 && (
                  <button
                    type="button"
                    className="cx-transfer-link"
                    aria-pressed={allColumns}
                    onClick={() => setAllColumns((value) => !value)}
                  >
                    {allColumns ? "Resumir prévia" : "Ver todas as colunas"}
                  </button>
                )}
              </div>
            </>
          )}
          <p className="cx-transfer-note">
            {columns ? `${columns} colunas no arquivo. ` : ""}Somente dados cadastrais, de contato e da negociação.
          </p>
        </section>
      </div>
    </Dialog>
  );
}
